import { useState, type FC } from 'react'

import { MessagePopup, StyledText } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { getWhiteLabelForDealerStyle } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import CloudDownloadOutlineIcon from 'mdi-react/CloudDownloadOutlineIcon'

import { useAppContext } from '@/App.context'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import { useSearchParamsByKey, useSortParams } from '@/hooks/searchParams'
import { useLocalDate } from '@/hooks/use-local-date'
import { exportDataToExcel } from '@/util/exportDataToExcel'

import { FormattedNumber } from '../FormattedNumber'

import { getCsvData } from './helpers'
import { queries } from './queries'
import type { SubOrder } from './resources'
import subscriptionOrderResources from './resources'
import type { CommonOrderKeys } from './types'

const TARGET_EXCEL_MAX_SIZE = 1000000

type ExportParams = Parameters<typeof exportDataToExcel>

type Props = {
  queryOrderType: string
  querySearch: string
  status: string
  count?: number
}

export const ExportToExcelOrders: FC<Props> = ({ queryOrderType, querySearch, status, count }) => {
  const { whiteLabel } = useAppContext()
  const { formatDate } = useLocalDate()
  const translate = useTranslation()
  const [{ querySort, querySortBy }] = useSortParams<CommonOrderKeys>()
  const [queryOrderTerminationType] = useSearchParamsByKey('terminationType')
  const [isExporting, setIsExporting] = useState(false)
  const [errorPopupMessage, setErrorPopupMessage] = useState<{
    text: string
    title: string
  } | null>(null)

  // Handle export process
  const handleExport = async (data: SubOrder[]) => {
    if (!data || data.length === 0) return

    try {
      const excelExportData = getCsvData(data, translate)
      const filename: ExportParams[1] = translate(
        `subscription-orders_${formatDate({ date: new Date() })}`
      )
      const options: ExportParams[2] = {
        columnFormats: {
          activationDate: 'date',
          terminationDate: 'date',
          price: 'amount',
          created: 'date',
          updated: 'date',
        },
      }

      await exportDataToExcel(excelExportData, filename, { ...options, translate })
    } catch (exportError) {
      setErrorPopupMessage({
        title: translate('Error Exporting Excel File'),
        text: translate(
          'We are sorry, but an error occurred while trying to export your Excel file. Please try again later.'
        ),
      })
    } finally {
      setIsExporting(false)
    }
  }

  const { isLoading: isLoadingPorting } = useQuery(
    queries.portingSubscriptionOrdersToExcel({
      keyword: querySearch,
      status,
      sortBy: querySortBy,
      sort: querySort,
    }),
    () =>
      subscriptionOrderResources.getPortingList({
        offset: 1,
        limit: TARGET_EXCEL_MAX_SIZE,
        keyword: querySearch,
        status,
        sortBy: querySortBy ?? undefined,
        sort: querySort && querySort !== 'off' ? querySort : undefined,
      }),
    {
      onSuccess(data) {
        if (isExporting) {
          handleExport(data)
        }
      },
      onError: () => {
        setIsExporting(false)
        setErrorPopupMessage({
          title: translate('Error Exporting Excel File'),
          text: translate(
            'We are sorry, but an error occurred while trying to export your Excel file. Please try again later.'
          ),
        })
      },
      enabled: isExporting && queryOrderType === 'porting',
      refetchOnWindowFocus: false,
    }
  )

  const { isLoading: isLoadingTermination } = useQuery(
    queries.terminationSubscriptionOrdersToExcel({
      keyword: querySearch,
      status,
      sortBy: querySortBy,
      sort: querySort,
      terminationType: queryOrderTerminationType || '',
    }),
    () =>
      subscriptionOrderResources.getTerminationList({
        offset: 1,
        limit: TARGET_EXCEL_MAX_SIZE,
        keyword: querySearch,
        status,
        sortBy: querySortBy ?? undefined,
        sort: querySort && querySort !== 'off' ? querySort : undefined,
        terminationType: queryOrderTerminationType || undefined,
      }),
    {
      onSuccess(data) {
        if (isExporting) {
          handleExport(data)
        }
      },
      onError: () => {
        setIsExporting(false)
        setErrorPopupMessage({
          title: translate('Error Exporting Excel File'),
          text: translate(
            'We are sorry, but an error occurred while trying to export your Excel file. Please try again later.'
          ),
        })
      },
      enabled: isExporting && queryOrderType === 'termination',
      refetchOnWindowFocus: false,
    }
  )

  const { isLoading: isLoadingUpgrade } = useQuery(
    queries.upgradeSubscriptionOrdersToExcel({
      keyword: querySearch,
      status,
      sortBy: querySortBy,
      sort: querySort,
    }),
    () =>
      subscriptionOrderResources.getUpgradeList({
        offset: 1,
        limit: TARGET_EXCEL_MAX_SIZE,
        keyword: querySearch,
        status,
        sortBy: querySortBy ?? undefined,
        sort: querySort && querySort !== 'off' ? querySort : undefined,
      }),
    {
      onSuccess(data) {
        if (isExporting) {
          handleExport(data)
        }
      },
      onError: () => {
        setIsExporting(false)
        setErrorPopupMessage({
          title: translate('Error Exporting Excel File'),
          text: translate(
            'We are sorry, but an error occurred while trying to export your Excel file. Please try again later.'
          ),
        })
      },
      enabled: isExporting && queryOrderType === 'upgrade',
      refetchOnWindowFocus: false,
    }
  )

  const handleExportClick = () => {
    setIsExporting(true)
  }
  const isLoading = isLoadingUpgrade || isLoadingPorting || isLoadingTermination
  const isButtonDisabled = isExporting && isLoading

  return (
    <>
      <Button
        className="mx-0 px-0"
        variant="tertiary"
        Icon={<CloudDownloadOutlineIcon />}
        disabled={isButtonDisabled}
        onClick={handleExportClick}
        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
      >
        {isButtonDisabled ? (
          <Translation id="Loading..." />
        ) : (
          <Translation
            id="Export {count} {count, plural, one {result} other {results}} to excel"
            count={
              // todo: Remove important when StyledText is refactored
              <StyledText
                className="!font-bold"
                whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
              >
                <FormattedNumber amount={count ?? 0} />
              </StyledText>
            }
          />
        )}
      </Button>

      <MessagePopup
        message={errorPopupMessage?.text}
        title={errorPopupMessage?.title}
        isOpen={Boolean(errorPopupMessage?.text)}
        onClose={() => setErrorPopupMessage(null)}
        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
      />
    </>
  )
}
